/**
 * web/src/components/PayslipPreview.tsx
 *
 * 給与明細プレビュー＆印刷コンポーネント（シンプル版）
 *
 * 表示項目:
 *   支給: 基本給 / 時間外手当 / 交通費（通勤手当・非課税）
 *         課税支給額合計 / 非課税支給額合計 / 総支給額
 *   控除: 所得税 / 控除額合計
 *   勤怠: 出勤日数 / 勤務時間 / 時間外労働時間
 *
 * 非表示:
 *   役職手当・資格手当・住居手当・家族手当・立替交通費
 *   健康保険・厚生年金・雇用保険・住民税・財形・生命保険料・積立金・組合費
 *   年末調整関連
 *   欠勤日数・有給日数・有給残日数
 *
 * プレビューと印刷/PDF保存で同じHTMLを使用する。
 * PDFファイル名は <title> 要素経由でブラウザの印刷ダイアログに渡される。
 */

import { useRef } from 'react'
import { useIsMobile } from '../hooks/useIsMobile'

// ================================================================
// 型
// ================================================================
export type PayrollRecord = {
  id: string
  assistant_id: string
  assistant_name: string
  payroll_month: string         // YYYY-MM
  pay_date: string | null
  basic_pay: number
  overtime_pay: number
  position_allowance: number
  qualification_allowance: number
  housing_allowance: number
  family_allowance: number
  commuting_allowance: number
  reimbursed_transportation: number
  other_expense_total: number
  other_allowance_total: number
  taxable_pay_total: number
  non_taxable_pay_total: number
  gross_pay_total: number
  health_insurance: number
  pension: number
  employment_insurance: number
  income_tax: number
  resident_tax: number
  property_savings: number
  life_insurance: number
  reserve_fund: number
  union_fee: number
  other_deductions: number
  deductions_total: number
  year_end_refund: number
  year_end_collection: number
  other_adjustment: number
  adjustment_total: number
  work_days: number
  absent_days: number
  paid_leave_days: number
  paid_leave_remaining: number
  work_minutes: number          // 勤務時間合計（分）
  overtime_minutes: number      // 時間外労働時間合計（分）
  net_pay: number
  remarks: string | null
  status: 'draft' | 'confirmed' | 'paid' | 'canceled'
  issuer_name: string
  issued_at: string | null
}

// ================================================================
// ヘルパー
// ================================================================

/** 金額を ¥カンマ区切り で表示（0 → ¥0） */
function fmtCurrency(n: number | null | undefined): string {
  return `¥${Number(n ?? 0).toLocaleString('ja-JP')}`
}

/** 分数を "XX時間YY分" 形式で表示 */
function fmtWorkMinutes(minutes: number | null | undefined): string {
  const m = Math.round(Number(minutes ?? 0))
  if (m <= 0) return '0時間00分'
  const h = Math.floor(m / 60)
  const min = m % 60
  return `${h}時間${String(min).padStart(2, '0')}分`
}

function escapeHtml(s: string | null | undefined): string {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

function parseYYYYMM(month: string): { year: number; month: number } {
  const [y, m] = (month ?? '').split('-').map(Number)
  return { year: y || 0, month: m || 0 }
}

function jstParts(d: Date): { year: number; month: number; day: number } {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0)
  return { year: get('year'), month: get('month'), day: get('day') }
}

function fmtYmd(y: number, m: number, d: number): string {
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d) || !y || !m || !d) return '—'
  return `${y}年${m}月${d}日`
}

// D1 の datetime('now') は UTC の "YYYY-MM-DD HH:mm:ss" で返ることがある。
// pay_date のような日付だけの値はそのまま扱い、時刻付きは JST 表示へ変換する。
function fmtPayslipDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  const raw = String(dateStr).trim()

  let m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) return fmtYmd(Number(m[1]), Number(m[2]), Number(m[3]))

  m = raw.match(/^(\d{4})-(\d{1,2})-(\d{1,2})[ T](\d{1,2}):(\d{2})(?::(\d{2}))?$/)
  if (m) {
    const isoUtc = `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}T${m[4].padStart(2, '0')}:${m[5]}:${(m[6] ?? '00').padStart(2, '0')}Z`
    const d = new Date(isoUtc)
    if (!Number.isNaN(d.getTime())) {
      const p = jstParts(d)
      return fmtYmd(p.year, p.month, p.day)
    }
  }

  const d = new Date(raw)
  if (!Number.isNaN(d.getTime())) {
    const p = jstParts(d)
    return fmtYmd(p.year, p.month, p.day)
  }

  return '—'
}

/** 対象月の末日を返す */
function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate()
}

/** ファイル名に使えない文字を除去する */
function sanitizeFilename(s: string): string {
  return s.replace(/[/\\?%*:|"<>]/g, '_').trim()
}

// ================================================================
// 帳票HTML生成
//   プレビューと印刷/PDF保存で必ず同じHTMLを使用する。
// ================================================================
function buildPayslipHTML(r: PayrollRecord, opts?: { autoPrint?: boolean }): string {
  const { year, month } = parseYYYYMM(r.payroll_month)
  const title = `${year}年${month}月分 給与明細書`

  // ファイル名として使えるタイトル（ブラウザの印刷ダイアログでデフォルトファイル名になる）
  const yyyymm = r.payroll_month.replace('-', '')
  const pdfTitle = `給与明細_${yyyymm}_${sanitizeFilename(r.assistant_name)}`

  const payDate    = fmtPayslipDate(r.pay_date)

  // 対象期間: YYYY年M月1日〜YYYY年M月末日
  const periodStart = fmtYmd(year, month, 1)
  const periodEnd   = fmtYmd(year, month, lastDayOfMonth(year, month))
  const period      = `${periodStart}〜${periodEnd}`

  const line = (label: string, value: string, total = false) => `
    <tr class="${total ? 'is-total' : ''}">
      <th>${escapeHtml(label)}</th>
      <td>${escapeHtml(value)}</td>
    </tr>`

  const attItem = (label: string, value: string) => `
    <div class="att-item">
      <span>${escapeHtml(label)}</span>
      <strong>${escapeHtml(value)}</strong>
    </div>`

  const autoPrintScript = opts?.autoPrint
    ? `<script>
    window.addEventListener('load', () => {
      setTimeout(() => { window.print() }, 300)
    })
  </script>`
    : ''

  // 備考テキスト（固定部分 + レコードのremarks）
  const baseRemarks = [
    'シフト制のため、基本給欄には通常勤務分賃金（月内の所定内勤務分）を記載し、',
    '時間外手当欄には残業相当分のみ記載する。',
    '交通費は通勤手当として支給とする。',
  ].join('\n')
  const fullRemarks = r.remarks
    ? `${baseRemarks}\n${r.remarks}`
    : baseRemarks

  return `<!DOCTYPE html>
<html lang="ja">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${escapeHtml(pdfTitle)}</title>
  <style>
    @page { size: A4; margin: 14mm 13mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic", "Meiryo", sans-serif;
      font-size: 11px;
      line-height: 1.5;
      color: #1f2933;
      background: #f3f4f6;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .document {
      width: 210mm;
      min-height: 297mm;
      margin: 20px auto;
      padding: 14mm 13mm;
      background: #fff;
      box-shadow: 0 8px 26px rgba(15, 23, 42, 0.14);
    }

    .top-rule { height: 4px; background: #111827; margin-bottom: 16px; }

    /* ヘッダー */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 24px;
      padding-bottom: 14px;
      border-bottom: 1px solid #d8dde3;
      margin-bottom: 14px;
    }
    .title-block .eyebrow {
      font-size: 9px;
      letter-spacing: 0.16em;
      color: #6b7280;
      font-weight: 700;
      margin-bottom: 3px;
    }
    .title {
      font-size: 20px;
      letter-spacing: 0.03em;
      font-weight: 800;
      color: #111827;
    }
    .issuer-block {
      text-align: right;
      color: #374151;
    }
    .issuer-label {
      font-size: 9px;
      color: #6b7280;
      font-weight: 700;
      letter-spacing: 0.08em;
      margin-bottom: 2px;
    }
    .issuer-name {
      font-size: 12px;
      font-weight: 700;
      color: #111827;
    }
    .status-chip {
      display: inline-block;
      margin-top: 6px;
      padding: 2px 8px;
      border: 1px solid #d1d5db;
      border-radius: 999px;
      color: #6b7280;
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.08em;
    }

    /* 受取人・対象情報 */
    .recipient {
      display: flex;
      gap: 20px;
      align-items: flex-start;
      margin-bottom: 14px;
      padding: 12px 14px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #fbfbfa;
    }
    .recipient-name {
      font-size: 18px;
      font-weight: 800;
      color: #111827;
      min-width: 130px;
      white-space: nowrap;
      padding-top: 2px;
    }
    .info-table {
      flex: 1;
      border-collapse: collapse;
    }
    .info-table th, .info-table td {
      border: none;
      padding: 3px 10px 3px 0;
      text-align: left;
      font-size: 11px;
      white-space: nowrap;
    }
    .info-table th {
      color: #6b7280;
      font-weight: 600;
      width: 72px;
    }
    .info-table td {
      color: #111827;
      font-weight: 600;
    }

    /* サマリー */
    .summary {
      display: grid;
      grid-template-columns: 1fr 1fr 1.35fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .summary-card {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      padding: 10px 12px;
      background: #fff;
    }
    .summary-card.primary {
      border-color: #d5e4d7;
      background: #f5faf6;
    }
    .summary-label {
      font-size: 9px;
      color: #6b7280;
      font-weight: 700;
      letter-spacing: 0.08em;
      margin-bottom: 4px;
    }
    .summary-value {
      font-size: 17px;
      text-align: right;
      font-weight: 800;
      color: #111827;
      font-variant-numeric: tabular-nums;
    }
    .summary-card.primary .summary-value {
      font-size: 22px;
      color: #1f6b3a;
    }

    /* 支給・控除セクション */
    .section-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-bottom: 14px;
    }
    .section {
      border: 1px solid #e1e5ea;
      border-radius: 10px;
      overflow: hidden;
      background: #fff;
    }
    .section-title {
      padding: 7px 11px;
      font-size: 10px;
      color: #111827;
      font-weight: 800;
      letter-spacing: 0.12em;
      background: #f3f4f6;
      border-bottom: 1px solid #e1e5ea;
    }
    table { width: 100%; border-collapse: collapse; font-size: 10.5px; }
    th, td {
      padding: 6px 10px;
      border-bottom: 1px solid #eef1f4;
      white-space: nowrap;
    }
    tr:last-child th, tr:last-child td { border-bottom: 0; }
    th { text-align: left; color: #4b5563; font-weight: 600; width: 62%; }
    td { text-align: right; color: #111827; font-weight: 600; font-variant-numeric: tabular-nums; }
    tr.is-total th, tr.is-total td {
      background: #fafafa;
      font-weight: 800;
      color: #111827;
      border-top: 1px solid #d8dde3;
    }

    /* 勤怠・振込支給額 */
    .attendance-net {
      display: grid;
      grid-template-columns: 1.4fr 1fr;
      gap: 14px;
      margin-bottom: 14px;
    }
    .attendance-box, .net-box {
      border: 1px solid #e1e5ea;
      border-radius: 10px;
      overflow: hidden;
      background: #fff;
    }
    .att-body {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0;
      padding: 10px;
    }
    .att-item {
      padding: 5px 8px;
      border-right: 1px solid #eef1f4;
    }
    .att-item:last-child { border-right: 0; }
    .att-item span {
      display: block;
      font-size: 9px;
      color: #6b7280;
      font-weight: 700;
      margin-bottom: 2px;
    }
    .att-item strong {
      font-size: 13px;
      color: #111827;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
    }
    .net-box {
      background: #f5faf6;
      border-color: #d5e4d7;
      display: flex;
      flex-direction: column;
      justify-content: center;
      padding: 12px 16px;
      text-align: right;
    }
    .net-label {
      font-size: 10px;
      color: #4b5563;
      font-weight: 800;
      letter-spacing: 0.1em;
      margin-bottom: 4px;
    }
    .net-value {
      font-size: 26px;
      font-weight: 900;
      color: #1f6b3a;
      font-variant-numeric: tabular-nums;
    }

    /* 備考 */
    .remarks {
      border: 1px solid #e1e5ea;
      border-radius: 10px;
      overflow: hidden;
      margin-bottom: 14px;
      background: #fff;
    }
    .remarks-body {
      padding: 10px 12px;
      white-space: pre-wrap;
      color: #374151;
      line-height: 1.65;
      font-size: 10px;
    }

    /* フッター */
    .footer {
      display: flex;
      justify-content: flex-end;
      gap: 28px;
      padding-top: 10px;
      border-top: 1px solid #d8dde3;
      color: #4b5563;
      font-size: 10.5px;
    }
    .footer strong { color: #111827; font-weight: 800; }

    /* 下書きウォーターマーク */
    .draft-mark {
      position: fixed;
      top: 42%;
      left: 29%;
      font-size: 68px;
      font-weight: 900;
      color: rgba(125, 32, 32, 0.09);
      transform: rotate(-28deg);
      pointer-events: none;
      letter-spacing: 0.14em;
    }

    @media print {
      body { background: #fff; }
      .document {
        width: auto;
        min-height: auto;
        margin: 0;
        padding: 0;
        box-shadow: none;
      }
    }
  </style>
</head>
<body>
  ${r.status === 'draft' ? '<div class="draft-mark">下書き</div>' : ''}
  <div class="document">
    <div class="top-rule"></div>

    <header class="header">
      <div class="title-block">
        <div class="eyebrow">PAYSLIP</div>
        <div class="title">${escapeHtml(title)}</div>
      </div>
      <div class="issuer-block">
        <div class="issuer-label">発行者</div>
        <div class="issuer-name">${escapeHtml(r.issuer_name)}</div>
        ${r.status === 'draft' ? '<div class="status-chip">DRAFT</div>' : ''}
      </div>
    </header>

    <!-- 受取人・対象情報 -->
    <section class="recipient">
      <div class="recipient-name">${escapeHtml(r.assistant_name)} 様</div>
      <table class="info-table">
        <tr>
          <th>対象期間</th>
          <td>${escapeHtml(period)}</td>
        </tr>
        <tr>
          <th>支払日</th>
          <td>${escapeHtml(payDate)}</td>
        </tr>
      </table>
    </section>

    <!-- 上部サマリー -->
    <section class="summary">
      <div class="summary-card">
        <div class="summary-label">総支給額</div>
        <div class="summary-value">${fmtCurrency(r.gross_pay_total)}</div>
      </div>
      <div class="summary-card">
        <div class="summary-label">控除額合計</div>
        <div class="summary-value">${fmtCurrency(r.deductions_total)}</div>
      </div>
      <div class="summary-card primary">
        <div class="summary-label">差引支給額</div>
        <div class="summary-value">${fmtCurrency(r.net_pay)}</div>
      </div>
    </section>

    <!-- 支給・控除 -->
    <section class="section-grid">
      <div class="section">
        <div class="section-title">支給</div>
        <table>
          ${line('基本給', fmtCurrency(r.basic_pay))}
          ${line('時間外手当', fmtCurrency(r.overtime_pay))}
          ${r.other_allowance_total ? line('その他手当（課税）', fmtCurrency(r.other_allowance_total)) : ''}
          ${line('課税支給額合計', fmtCurrency(r.taxable_pay_total), true)}
          ${line('交通費（通勤手当・非課税）', fmtCurrency(r.commuting_allowance))}
          ${r.other_expense_total ? line('その他経費（非課税）', fmtCurrency(r.other_expense_total)) : ''}
          ${line('非課税・経費支給額合計', fmtCurrency(r.non_taxable_pay_total), true)}
          ${line('総支給額', fmtCurrency(r.gross_pay_total), true)}
        </table>
      </div>

      <div class="section">
        <div class="section-title">控除</div>
        <table>
          ${line('所得税', fmtCurrency(r.income_tax))}
          ${line('控除額合計', fmtCurrency(r.deductions_total), true)}
        </table>
      </div>
    </section>

    <!-- 勤怠・振込支給額 -->
    <section class="attendance-net">
      <div class="attendance-box">
        <div class="section-title">勤怠</div>
        <div class="att-body">
          ${attItem('出勤日数', `${Number(r.work_days).toLocaleString('ja-JP')}日`)}
          ${attItem('勤務時間', fmtWorkMinutes(r.work_minutes))}
          ${attItem('時間外労働時間', fmtWorkMinutes(r.overtime_minutes))}
        </div>
      </div>
      <div class="net-box">
        <div class="net-label">振込支給額</div>
        <div class="net-value">${fmtCurrency(r.net_pay)}</div>
      </div>
    </section>

    <!-- 備考 -->
    <section class="remarks">
      <div class="section-title">備考</div>
      <div class="remarks-body">${escapeHtml(fullRemarks)}</div>
    </section>

    <footer class="footer">
      <div>発行者　<strong>${escapeHtml(r.issuer_name)}</strong></div>
    </footer>
  </div>
  ${autoPrintScript}
</body>
</html>`
}

// ================================================================
// 印刷ウィンドウを開く
// ================================================================
export function openPayslipPrint(record: PayrollRecord) {
  const html = buildPayslipHTML(record, { autoPrint: true })
  const win  = window.open('', '_blank', 'width=900,height=1200')
  if (!win) {
    alert('ポップアップがブロックされています。ブラウザの設定でポップアップを許可してください。')
    return
  }
  win.document.write(html)
  win.document.close()
}

// ================================================================
// プレビューモーダル（印刷と同じHTMLを iframe で表示）
// ================================================================
type Props = {
  record: PayrollRecord
  onClose: () => void
  onLogIssue?: () => void
}

export function PayslipPreview({ record, onClose, onLogIssue }: Props) {
  const { year, month } = parseYYYYMM(record.payroll_month)
  const previewHtml = buildPayslipHTML(record, { autoPrint: false })
  const isMobile = useIsMobile()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  // 可能なら window.open のポップアップに頼らず、既に表示中の iframe を
  // そのまま印刷する（モバイルブラウザのポップアップブロック回避）。
  // iframe の contentWindow が取得できない場合のみ従来の別ウィンドウ方式にフォールバックする。
  const handlePrint = () => {
    const win = iframeRef.current?.contentWindow
    if (win) {
      win.focus()
      win.print()
    } else {
      openPayslipPrint(record)
    }
    onLogIssue?.()
  }

  return (
    <div className="overlay" onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{ alignItems: isMobile ? 'flex-end' : 'center' }}>
      <div className="modal" style={{
        display: 'flex',
        flexDirection: 'column',
        maxWidth: isMobile ? '100%' : 980,
        width: isMobile ? '100%' : '94vw',
        margin: isMobile ? 0 : undefined,
        borderRadius: isMobile ? '14px 14px 0 0' : undefined,
        maxHeight: isMobile ? '90dvh' : '90vh',
        height: isMobile ? '90dvh' : '90vh',
        overflow: 'hidden',
        ...(isMobile ? { position: 'fixed' as const, bottom: 0, left: 0, right: 0 } : {}),
      }}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <div className="modal-title">
            給与明細プレビュー — {year}年{month}月 / {record.assistant_name}
            {record.status === 'draft' && (
              <span className="badge badge-gray" style={{ marginLeft: 8, fontSize: 10 }}>下書き</span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body" style={{ flex: 1, minHeight: 0, padding: 0, background: '#f3f4f6' }}>
          <iframe
            ref={iframeRef}
            title="給与明細プレビュー"
            srcDoc={previewHtml}
            style={{ width: '100%', height: '100%', border: 0, display: 'block', background: '#f3f4f6' }}
          />
        </div>

        <div className="modal-footer" style={{
          flexShrink: 0,
          paddingBottom: isMobile ? 'calc(14px + env(safe-area-inset-bottom, 0px))' : undefined,
        }}>
          <button className="btn btn-outline" onClick={onClose}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
            閉じる
          </button>
          <button className="btn btn-primary" onClick={handlePrint}
            style={{ width: isMobile ? '100%' : undefined, height: isMobile ? 48 : undefined }}>
            🖨 印刷 / PDFとして保存
          </button>
        </div>
      </div>
    </div>
  )
}
